import { useEffect, useState } from 'react'
import { useCatalogoApi } from '../catalogo/api'
import { t, Lang } from './i18n'
import { t as tMinutaje, umbralMinutaje, UMBRAL_COLOR } from '../minutaje/i18n'
import { DashboardMiembro } from '../minutaje/model'
import { EmptyState } from '../styles/EmptyState'
import { Spinner } from '../styles/Spinner'
import type { EquipoEstado } from './PanelPage'
import styles from './PanelJugadoresTab.module.css'

const CATEGORIAS_MINUTAJE = ['f7', 'f11'] as const

function Pct({ pct }: { pct: number | '--' }) {
  return <span style={{ color: UMBRAL_COLOR[umbralMinutaje(pct)] }}>{pct === '--' ? '--' : `${pct}%`}</span>
}

// Issue #77 (club, 2026-10-22): "dentro de ALERTA… una nueva pestaña JUGADORES. Deben aparecer todos los
// equipos bien organizados, diferenciando visualmente si son de F7 o de F11… al hacer clic en un equipo,
// se muestran únicamente los jugadores que ya están marcados como alerta por tener un porcentaje bajo de
// minutos, usando los mismos datos y la misma lógica". Los datos son los del dashboard de minutaje
// (GET /panel/jugadores-alerta/:equipoId), sin recalcular nada aquí.
export function PanelJugadoresTab({ equipos, lang }: { equipos: EquipoEstado[]; lang: Lang }) {
  const api = useCatalogoApi()
  const [seleccionado, setSeleccionado] = useState<EquipoEstado | null>(null)
  const [jugadores, setJugadores] = useState<DashboardMiembro[] | null>(null)

  useEffect(() => {
    if (!seleccionado) return
    setJugadores(null)
    api.get(`/panel/jugadores-alerta/${seleccionado.id}`).then(setJugadores)
  }, [seleccionado?.id])

  const conMinutaje = equipos.filter((e) => (CATEGORIAS_MINUTAJE as readonly string[]).includes(e.categoria))
  if (conMinutaje.length === 0) return <EmptyState text={t('sinEquiposMinutaje', lang)} icon="🛡️" />

  return (
    <div className={styles.contenedor}>
      {CATEGORIAS_MINUTAJE.map((categoria) => {
        const lista = conMinutaje.filter((e) => e.categoria === categoria)
        if (lista.length === 0) return null
        return (
          <section key={categoria} className={styles.seccion}>
            <h3 className={`${styles.categoria} ${styles[categoria]}`}>{t(categoria, lang)}</h3>
            <div className={styles.equipos}>
              {lista.map((equipo) => (
                <button
                  key={equipo.id}
                  className={`${styles.equipo} ${styles[categoria]}`}
                  aria-pressed={seleccionado?.id === equipo.id}
                  onClick={() => setSeleccionado(equipo)}
                >
                  <span className={styles.badge}>{t(categoria, lang)}</span>
                  {equipo.nombre}
                </button>
              ))}
            </div>
          </section>
        )
      })}

      <section className={styles.resultado} aria-live="polite">
        {!seleccionado ? (
          <p className={styles.ayuda}>{t('eligeEquipoJugadores', lang)}</p>
        ) : (
          <>
            <h3>{seleccionado.nombre}</h3>
            {jugadores === null ? (
              <Spinner />
            ) : jugadores.length === 0 ? (
              <EmptyState text={t('sinJugadoresAlerta', lang)} icon="✅" />
            ) : (
              <div className="table-scroll">
                <table className="sticky-first-col" aria-label={`${t('vistaJugadores', lang)} — ${seleccionado.nombre}`}>
                  <thead>
                    <tr>
                      <th>{tMinutaje('jugador', lang)}</th>
                      <th>{tMinutaje('porcDisp', lang)}</th>
                      <th>{tMinutaje('porcTotal', lang)}</th>
                      <th>{tMinutaje('estado', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jugadores.map((m) => (
                      <tr key={m.miembro_equipo_id}>
                        <td>{m.persona.nombre}</td>
                        <td>
                          <Pct pct={m.porcentaje_disp} />
                        </td>
                        <td>
                          <Pct pct={m.porcentaje_total} />
                        </td>
                        <td>
                          <span className={m.alerta === 'intervenir' ? 'pill pill-danger' : 'pill pill-warning'}>
                            {m.alerta === 'intervenir' ? tMinutaje('alertaIntervenir', lang) : tMinutaje('alertaVigilar', lang)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className={styles.ayuda}>{tMinutaje('leyendaExplicacion', lang)}</p>
          </>
        )}
      </section>
    </div>
  )
}
